import React, { useCallback, useMemo } from 'react';
import { AutoSizer, List, ListRowProps } from 'react-virtualized';
import { FieldPill } from '../../../field-pill';
import {
  FieldPillGroupStyled,
  FieldPillStyled,
  HiddenFieldsHeader,
} from '../../viz-layout-panel.styles';
import { FieldDropdown } from '../../../../components/field-dropdown';
import { IAnyAttribute } from '../../../../datasets/interfaces/attribute.interface';
import { useVizLayoutPanelContext } from '../../viz-layout-panel.context';

interface VirtualizedFieldListProps {
  onContextMenuAction: (key: string, field: IAnyAttribute) => void;
  isScrolling: boolean;
  expandTimeFields: string[];
  onExpandToggleHandler: (fieldName: string) => void;
}

export const VirtualizedFieldList: React.FC<VirtualizedFieldListProps> = ({
  onContextMenuAction,
  isScrolling,
  expandTimeFields,
  onExpandToggleHandler,
}) => {
  const { viz, getVirtualizationData, isSearchFieldsValueValid } =
    useVizLayoutPanelContext();

  // Get virtualization data from context
  const { flatFieldList } = useMemo(
    () => getVirtualizationData(expandTimeFields),
    [getVirtualizationData, expandTimeFields],
  );

  const rowRenderer = useCallback(
    ({ index, key, style }: ListRowProps) => {
      const item = flatFieldList[index];
      if (!item) {
        return null;
      }

      const { field, type, depth } = item;
      const paddingLeft = depth * 20; // Indent child fields

      // Handle separator for hidden fields
      if (type === 'separator') {
        return (
          <div key={key} style={style}>
            <HiddenFieldsHeader>
              {field.displayName || 'Hidden Fields'}
            </HiddenFieldsHeader>
          </div>
        );
      }

      if (type === 'parent') {
        // Render parent field (hierarchy root)
        const fieldPillRoot = (
          <FieldPill
            field={field}
            iconDataType={'HIERARCHY'}
            key={`${field.name}-${index}`}
            renderFieldDropdown={isDragging => (
              <FieldDropdown
                discoveryId={viz?.id}
                field={field}
                doMenuAction={actionKey =>
                  onContextMenuAction(actionKey, field)
                }
                isScrolling={isScrolling}
                isDragging={isDragging}
              />
            )}
          />
        );

        const forceExpanded = expandTimeFields.includes(field.name);
        return (
          <div key={key} style={style}>
            <div style={{ paddingLeft }}>
              <FieldPillGroupStyled
                root={fieldPillRoot}
                expanded={forceExpanded || isSearchFieldsValueValid}
                onExpandToggle={() => onExpandToggleHandler(field.name)}
                isScrolling={isScrolling}
              />
            </div>
          </div>
        );
      }

      if (type === 'child') {
        return (
          <div key={key} style={style}>
            <div style={{ paddingLeft }}>
              <FieldPill
                field={field}
                key={`${field.name}-${index}`}
                renderFieldDropdown={isDragging => (
                  <FieldDropdown
                    discoveryId={viz?.id}
                    field={field}
                    doMenuAction={actionKey =>
                      onContextMenuAction(actionKey, field)
                    }
                    isScrolling={isScrolling}
                    isDragging={isDragging}
                  />
                )}
              />
            </div>
          </div>
        );
      }

      return (
        <div key={key} style={style}>
          <div style={{ paddingLeft }}>
            <FieldPillStyled
              field={field}
              key={`${field.name}-${index}`}
              renderFieldDropdown={isDragging => (
                <FieldDropdown
                  discoveryId={viz?.id}
                  field={field}
                  doMenuAction={actionKey =>
                    onContextMenuAction(actionKey, field)
                  }
                  isScrolling={isScrolling}
                  isDragging={isDragging}
                />
              )}
            />
          </div>
        </div>
      );
    },
    [
      flatFieldList,
      viz?.id,
      onContextMenuAction,
      isScrolling,
      expandTimeFields,
      onExpandToggleHandler,
      isSearchFieldsValueValid,
    ],
  );

  return (
    <div
      className='virtualized-field-list'
      role='list'
      style={{ height: '100%' }}
    >
      <AutoSizer>
        {({ height, width }) => (
          <List
            height={height}
            width={width}
            rowCount={flatFieldList.length}
            rowHeight={22}
            rowRenderer={rowRenderer}
            overscanRowCount={10}
            style={{
              outline: 'none',
            }}
          />
        )}
      </AutoSizer>
    </div>
  );
};
